import {
  transformCEConversationsToMetadatas,
  transformCEConversationToConversation,
  transformCEConversationToMetadata,
} from "@mirage/service-conversation/service/conversationsSync/ceConverters";
import { callApiV2 } from "@mirage/service-dbx-api";
import { sleepMs } from "@mirage/shared/util/tiny-utils";

import type { context_engine } from "@dropbox/api-v2-client/types/dropbox_types";
import type { DataAPI } from "@mirage/service-conversation/service/conversationsSync/types";

export function createAPIAdapters(): DataAPI {
  // TOOD: add optimization to pagination to only fetch new conversations
  const listMetadata: DataAPI["listMetadata"] = async (maxCount) => {
    let cursor: string | undefined = undefined;
    const allCEConversations: context_engine.UserConversation[] = [];
    do {
      const response: context_engine.ListUserConversationsResponse =
        await callApiV2("contextEngineConversationsListUserConversations", {
          cursor,
        });
      cursor = response.cursor;
      allCEConversations.push(...(response.conversations || []));
      if (maxCount && allCEConversations.length >= maxCount) {
        break; // already got enough recent conversations
      }
      if (cursor) {
        await sleepMs(250); // Add delay to avoid rate limiting
      }
    } while (cursor);
    return transformCEConversationsToMetadatas(allCEConversations);
  };

  const getMetadata: DataAPI["getMetadata"] = async (serverConversationId) => {
    const response = await callApiV2(
      "contextEngineConversationsGetUserConversation",
      {
        conversation_id: serverConversationId,
      },
    );
    return response.conversation
      ? transformCEConversationToMetadata(response.conversation)
      : undefined;
  };

  const getConversation: DataAPI["getConversation"] = async (
    conversationID,
  ) => {
    let cursor: string | undefined = undefined;
    let ceConversation: context_engine.UserConversation | undefined;
    const allCEMessages: context_engine.ConversationsChatMessage[] = [];
    do {
      const params: context_engine.GetUserConversationArg = {
        conversation_id: conversationID,
        cursor: cursor,
      };
      const response = await callApiV2(
        "contextEngineConversationsGetUserConversation",
        params,
      );
      cursor = response.cursor;
      ceConversation = response.conversation;
      allCEMessages.push(...(response.messages || []));
    } while (cursor);

    if (!ceConversation) {
      throw new Error("No conversation from getUserConversation");
    }
    return transformCEConversationToConversation(ceConversation, allCEMessages);
  };

  const updateMetadata: DataAPI["updateMetadata"] = async (
    serverId,
    metadata,
  ) => {
    const args: context_engine.UpdateUserConversationArg = {
      conversation_id: serverId,
      title: metadata.title,
    };
    const response = await callApiV2(
      "contextEngineConversationsUpdateUserConversation",
      args,
    );
    if (!response.conversation) {
      throw new Error("No conversation from updateUserConversation");
    }
    const updatedMetadata = transformCEConversationToMetadata(
      response.conversation,
    );
    if (!updatedMetadata) {
      throw new Error("Invalid metadata from updateUserConversation");
    }
    return updatedMetadata;
  };

  const updateConversationDraft: DataAPI["updateConversationDraft"] = async (
    serverId,
    draft,
  ) => {
    const args: context_engine.UpdateDraftArg = {
      conversation_id: serverId,
      draft_content: draft,
    };
    const response = await callApiV2(
      "contextEngineConversationsUpdateDraft",
      args,
    );
    if (!response.conversation) {
      throw new Error("No conversation from updateDraft");
    }
    const updatedMetadata = transformCEConversationToMetadata(
      response.conversation,
    );
    if (!updatedMetadata) {
      throw new Error("Invalid metadata from updateDraft");
    }
    return updatedMetadata;
  };

  const deleteConversations: DataAPI["deleteConversations"] = async (
    serverIds,
  ) => {
    const args: context_engine.DeleteUserConversationsArg = {
      conversation_ids: serverIds,
    };
    await callApiV2("contextEngineConversationsDeleteUserConversations", args);
  };

  return {
    listMetadata,
    getMetadata,
    getConversation,
    updateMetadata,
    updateConversationDraft,
    deleteConversations,
  };
}
